//AIModelSelect.tsx
import { compareModelsByFamily } from './utils/modelFamily'
import React from 'react'
import { Select, Spin, Tooltip, Tag } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import type { OrderedModel } from './AIModelConfig'
import { useOrderedModels, renderModelOption, findPreferredOrderedModel } from './AIModelConfig'
import { isModelDisabledInLanguage } from './utils/aiModelService'

interface AIModelSelectProps {
  value?: string
  onChange: (value: string) => void
  language?: string
  sourceLanguage?: string
  isLangTesterUser?: boolean
  style?: React.CSSProperties
  placeholder?: string
  disabled?: boolean
  modelOptions?: OrderedModel[]
  modelOptionsLoading?: boolean
  autoSelect?: boolean
  extraOptions?: Array<{
    value: string
    label: React.ReactNode
    disabled?: boolean
  }>
  respectValue?: boolean
  preferredModelId?: string
  workflow?: 'translation' | 'proofreading' | 'metadata' | 'blurb' | 'keywords' | string
}

interface AIModelSelectBaseProps extends Omit<AIModelSelectProps, 'modelOptions' | 'modelOptionsLoading'> {
  orderedModels: OrderedModel[]
  loading: boolean
}

const AIModelSelectBase: React.FC<AIModelSelectBaseProps> = ({
  value,
  onChange,
  language,
  sourceLanguage,
  isLangTesterUser = false,
  style = { width: '100%' },
  placeholder = 'Choose a Language to select an AI Model',
  disabled = false,
  autoSelect = true,
  extraOptions = [],
  respectValue = false,
  preferredModelId,
  workflow,
  orderedModels,
  loading,
}) => {
  const debugModels = React.useMemo(() => {
    try {
      return localStorage.getItem('debugProofreadModal') === 'true'
    } catch (_e) {
      return false
    }
  }, [])
  const debugStamp = React.useCallback((label: string) => {
    if (!debugModels) return
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
    console.log(`[AIModelSelect] ${label} @ ${Math.round(now)}ms`)
  }, [debugModels])

  React.useEffect(() => {
    if (!debugModels) return
    const lang = language || 'none'
    debugStamp(`render lang=${lang} loading=${loading} models=${orderedModels.length} value=${value || 'none'}`)
  }, [debugModels, debugStamp, language, loading, orderedModels.length, value])

  const preferredId = React.useMemo(() => (preferredModelId || '').trim(), [preferredModelId])

  const isDisabled = React.useCallback(
    (model: any) => isModelDisabledInLanguage(model, language),
    [language]
  )

  const displayModels = React.useMemo(
    () => [...orderedModels].sort((a, b) => compareModelsByFamily(a.model, b.model)),
    [orderedModels]
  )

  const availableModels = React.useMemo(
    () => displayModels.filter(({ model }) =>
      (!model.testingOnly || isLangTesterUser) && !isDisabled(model)
    ),
    [displayModels, isLangTesterUser, isDisabled]
  )

  const preferredCandidate = React.useMemo(
    () => findPreferredOrderedModel(availableModels, preferredModelId),
    [availableModels, preferredModelId]
  )

  const workflowDefault = React.useMemo(() => {
    if (!workflow) return null
    return availableModels.find(({ model }) =>
      (model.defaultForWorkflows || []).includes(workflow)
    ) || null
  }, [availableModels, workflow])

  const preferredExtraOption = extraOptions.find(option =>
    option.value === preferredId && !option.disabled
  )
  const defaultModelId = preferredExtraOption?.value || preferredCandidate?.model.id ||
    workflowDefault?.model.id || availableModels[0]?.model.id || ''

  const userSelectedSinceLanguageChangeRef = React.useRef<boolean>(false)
  const previousLanguageRef = React.useRef<string | undefined>(language)

  React.useEffect(() => {
    userSelectedSinceLanguageChangeRef.current = false
  }, [language, sourceLanguage])

  React.useEffect(() => {
    const hadLanguage = !!previousLanguageRef.current
    const hasLanguage = !!language
    previousLanguageRef.current = language
    if (hadLanguage && !hasLanguage && value) {
      onChange('')
    }
  }, [language, value, onChange])

  React.useEffect(() => {
    if (!language) {
      return
    }
  }, [language])

  React.useEffect(() => {
    if (!autoSelect) return
    if (!language || loading) return
    if (userSelectedSinceLanguageChangeRef.current) return

    debugStamp(`defaultModel:check lang=${language} models=${orderedModels.length}`)
    if (value && extraOptions.some(option => option.value === value && !option.disabled)) {
      return
    }

    const modelExists = value
      ? availableModels.some(({ model }) => model.id === value) ||
        extraOptions.some(option => option.value === value && !option.disabled)
      : false

    if (respectValue && value && modelExists) {
      return
    }

    if (value && !modelExists) {
      onChange(defaultModelId)
    }

  }, [
    autoSelect,
    language,
    loading,
    orderedModels,
    value,
    onChange,
    defaultModelId,
    availableModels,
    extraOptions,
    respectValue,
  ])

  React.useEffect(() => {
    if (!autoSelect) return
    if (!loading && language && !value) {
      debugStamp(`defaultModel:set lang=${language} models=${orderedModels.length}`)
      if (defaultModelId) {
        onChange(defaultModelId)
      }
    }
  }, [autoSelect, loading, language, defaultModelId, value, onChange])

  return (
    <Select
      value={(!language || loading) ? undefined : (value || undefined)}
      onChange={(val) => {
        userSelectedSinceLanguageChangeRef.current = true
        onChange(val)
      }}
      style={style}
      placeholder={placeholder}
      disabled={disabled || loading || !language}
      loading={loading}
      notFoundContent={loading ? <Spin size="small" /> : (!language ? 'Please select a language first' : 'No models available')}
      suffixIcon={
        loading ? (
          <Spin size="small" />
        ) : (
          <span className="ant-select-suffix">
            <DownOutlined style={{ color: '#1677ff', pointerEvents: 'none' }} />
          </span>
        )
      }
    >
      {extraOptions.map(option => (
        <Select.Option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </Select.Option>
      ))}
      {displayModels.map(({ model }: OrderedModel) => {
        const disabledForLang = isDisabled(model)
        return (
          <Select.Option key={model.id} value={model.id} disabled={disabledForLang}>
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              {disabledForLang ? (
                <>
                  <span>{model.displayName}</span>
                  <Tooltip title="Unavailable due to poor performance with this language">
                    <Tag color="red" style={{ marginLeft: 8 }}>Disabled</Tag>
                  </Tooltip>
                </>
              ) : (
                renderModelOption(model)
              )}
            </span>
          </Select.Option>
        )
      })}
    </Select>
  )
}

const ConnectedAIModelSelect: React.FC<Omit<AIModelSelectProps, 'modelOptions' | 'modelOptionsLoading'>> = (props) => {
  const { language, sourceLanguage, isLangTesterUser = false } = props
  const { orderedModels, loading } = useOrderedModels(language, isLangTesterUser, sourceLanguage)
  return (
    <AIModelSelectBase
      {...props}
      orderedModels={orderedModels}
      loading={loading}
    />
  )
}

const AIModelSelect: React.FC<AIModelSelectProps> = ({
  modelOptions,
  modelOptionsLoading,
  ...rest
}) => {
  if (modelOptions) {
    return (
      <AIModelSelectBase
        {...rest}
        orderedModels={modelOptions}
        loading={modelOptionsLoading ?? false}
      />
    )
  }
  return <ConnectedAIModelSelect {...rest} />
}

export default AIModelSelect
